import React, { useState, useEffect, useMemo } from 'react'
import {
  Button,
  Input,
  message,
  Drawer,
  Slider,
  Tooltip,
  Spin,
  Modal
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import './ImageGeneration.scss'
import { ImagePreview } from '../../../../components'
import CommentInput from '../CommentInput'
import { generateImageRunway, getTaskStatus } from '../../../../services/api/aiVideoGeneration'
import StepTitle from '../StepTitle'
import { scenes as defaultScenes } from '../VideoGeneration/helper/data.video'

const { TextArea } = Input

const ImageGeneration = ({ formData, setFormData, onValidationChange }) => {
  const [scenes, setScenes] = useState([])
  const [selectedSettings, setSelectedSettings] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  //debug
  // useEffect(() => {
  //   console.log('defaultScenes', defaultScenes)
  //   setScenes(defaultScenes)
  // }, [])

  useEffect(() => {
    if (formData.shotList && formData.shotList.length > 0) {
      setScenes(formData.shotList)
      if (
        formData.shotList.some(scene =>
          scene.shots.some(shot => !shot.imageSrc)
        )
      ) {
        generateAllImages(formData.shotList)
      } else {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (isLoading) return
    setFormData(prev => ({ ...prev, shotList: scenes }))
    const allImagesGenerated = scenes.every(scene =>
      scene.shots.every(shot => shot.imageSrc)
    )
    onValidationChange(allImagesGenerated)
  }, [scenes, isLoading])

  const updateShotState = (
    prevScenes,
    sceneId,
    shotId,
    isGenerating,
    progress,
    url,
    error
  ) =>
    prevScenes.map(scene =>
      scene.id === sceneId
        ? {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    isGenerating,
                    progress,
                    ...(url && { imageSrc: url }),
                    ...(error !== undefined ? { error } : {})
                  }
                : shot
            )
          }
        : scene
    )

  const generateAllImages = async allScenes => {
    setIsLoading(true)
    try {
      const queue = allScenes.flatMap(scene =>
        scene.shots
          .filter(shot => !shot.imageSrc)
          .map(shot => ({ scene, shot }))
      )

      // Create all promises but don't wait for them individually
      const promises = queue.map(async ({ scene, shot }) => {
        try {
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, true, 0, '', null)
          )
          const payload = createPayload(shot, formData)
          const response = await generateImageRunway(payload)
          const taskId = response && response.data && response.data.task_id

          const outputUrl = await waitForTaskSuccess(taskId)

          setScenes(prev =>
            updateShotState(
              prev,
              scene.id,
              shot.id,
              false,
              100,
              outputUrl,
              null
            )
          )
        } catch (error) {
          // Handle individual request errors
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, false, 0, '', {
              message: (error && error.message) || 'Generation failed'
            })
          )
          console.error(
            `Error generating image for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
        }
      })

      // Wait for ALL promises to complete (both successful and failed)
      await Promise.allSettled(promises)
    } catch (error) {
      message.error('Error generating images')
    } finally {
      setIsLoading(false) // This runs only after all requests are done
    }
  }

  const generateAllImagesSequentially = async allScenes => {
    setIsLoading(true)
    try {
      const queue = allScenes.flatMap(scene =>
        scene.shots
          .filter(shot => !shot.imageSrc)
          .map(shot => ({ scene, shot }))
      )

      for (const { scene, shot } of queue) {
        try {
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, true, 0, '', null)
          )
          const payload = createPayload(shot, formData)
          const response = await generateImageRunway(payload)
          const taskId = response && response.data && response.data.task_id

          const outputUrl = await waitForTaskSuccess(taskId)

          setScenes(prev =>
            updateShotState(
              prev,
              scene.id,
              shot.id,
              false,
              100,
              outputUrl,
              null
            )
          )
        } catch (error) {
          setScenes(prev =>
            updateShotState(prev, scene.id, shot.id, false, 0, '', {
              message: (error && error.message) || 'Generation failed'
            })
          )
          console.error(
            `Error generating image for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
        }
      }
    } catch (error) {
      message.error('Error generating images')
    } finally {
      setIsLoading(false)
    }
  }

  function getPromptingNames(charactersData = [], selectedCharacters = []) {
    return selectedCharacters
      .map(selected => {
        // Find the character by matching the current name
        const characterName = selected.current.replace(/__/g, '') // Remove underscores
        const character = charactersData.find(
          char => char.name === characterName
        )

        if (character && character.attributes.prompting_name) {
          // Remove @ symbol if present
          return character.attributes.prompting_name.replace('@', '')
        }

        return null // Return null if character not found
      })
      .filter(name => name !== null) // Filter out null values
  }

  const createPayload = (shot, formData) => {
    const sceneData = {
      title: shot.title,
      location: shot.location,
      scenery: shot.scenery,
      dialogue: shot.dialogue
    }

    const revisedShotDirection = getRevisedShotDirection(shot)

    sceneData['shot direction'] = revisedShotDirection

    console.log('sceneData', sceneData)

    const finalSceneData = replaceCharacterNames(sceneData, shot, formData)

    const isRealistic =
      formData.videoStyle &&
      formData.videoStyle.key &&
      formData.videoStyle.key === 'realistic'

    return {
      scene_data: { [shot.key]: finalSceneData },
      characters: getPromptingNames(
        formData.characterList,
        formData.selectedCharacter
      ),
      ratio: '1024:1024',
      style: isRealistic
        ? null
        : (formData.videoStyle && formData.videoStyle.key) || null
    }
  }

  const getRevisedShotDirection = shot => {
    const { shot_direction, lighting = '', weather = '', temperature } = shot

    const details = []

    if (lighting) details.push(`Lighting: ${lighting}`)
    if (weather) details.push(`Weather: ${weather}`)
    if (temperature !== null && temperature !== undefined) {
      details.push(`Temperature/Creativity: ${temperature}`)
    }

    const revisedShotDirection = `${shot_direction} ${details.join(' ')}`.trim()

    return revisedShotDirection
  }

  const replaceCharacterNames = (obj, shot, formData) => {
    if (typeof obj === 'string') {
      if (!formData.selectedCharacter || !formData.characterList) {
        return obj
      }

      return formData.selectedCharacter.reduce((acc, selected) => {
        // Extract character name from current field (remove underscores)
        const characterName = selected.current.replace(/__/g, '')

        // Find character details by name
        const char = formData.characterList.find(c => c.name === characterName)

        if (char && char.attributes.prompting_name) {
          // Remove @ symbol from prompting name
          const promptingName = char.attributes.prompting_name.replace('@', '')

          // Replace character name with prompting name (case insensitive)
          acc = acc.replace(
            new RegExp(`\\b${char.name}\\b`, 'gi'),
            promptingName
          )

          return acc
        }

        return acc
      }, obj)
    }

    if (typeof obj === 'object' && obj !== null) {
      if (Array.isArray(obj)) {
        return obj.map(item => replaceCharacterNames(item, shot, formData))
      }

      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [
          k,
          replaceCharacterNames(v, shot, formData)
        ])
      )
    }

    return obj
  }

  const handleRegenerateImage = async (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene && scene.shots.find(s => s.id === shotId)

    if (!shot) return

    try {
      setScenes(prev => updateShotState(prev, sceneId, shotId, true, 0, '', null))

      const payload = createPayload(shot, formData)
      const response = await generateImageRunway(payload)
      const taskId = response && response.data && response.data.task_id
      const outputUrl = await waitForTaskSuccess(taskId)

      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, false, 100, outputUrl, null)
      )
    } catch (error) {
      message.error('Regeneration failed')
      console.error('Regeneration failed', error)
      setScenes(prev =>
        updateShotState(prev, sceneId, shotId, false, 0, '', {
          message: (error && error.message) || 'Generation failed'
        })
      )
    }
  }

  async function waitForTaskSuccess(taskId) {
    if (!taskId) {
      throw new Error('Missing task id')
    }

    const maxAttempts = 60
    const delayMs = 10000

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResponse = await getTaskStatus(taskId)
        const data = statusResponse && statusResponse.data ? statusResponse.data : null
        const status = data && data.status ? data.status : null

        if (status === 'success') {
          return data && data.output ? data.output : ''
        }

        if (status === 'failed' || status === 'error') {
          const err = new Error((data && data.error) || 'Task failed')
          err._isTerminal = true
          throw err
        }
      } catch (err) {
        // If the task failed terminally, stop immediately
        if (err && (err._isTerminal === true || err.message === 'Task failed')) {
          throw err
        }
        // If a polling attempt fails transiently, continue until maxAttempts
        if (attempt === maxAttempts - 1) {
          throw err
        }
      }

      await new Promise(resolve => setTimeout(resolve, delayMs))
    }

    throw new Error('Task polling timed out')
  }

  const handleEditSettings = (sceneId, shotId) => {
    const scene = scenes.find(s => s.id === sceneId)
    const shot = scene.shots.find(sh => sh.id === shotId)

    setSelectedSettings({
      sceneId,
      shotId,
      ...scene.settings,
      ...shot
    })
    setDrawerVisible(true)
  }

  const updateSettings = (key, value) => {
    setSelectedSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const saveSettings = () => {
    setScenes(prevScenes =>
      prevScenes.map(scene => {
        if (scene.id === selectedSettings.sceneId) {
          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === selectedSettings.shotId
                ? {
                    ...shot,
                    shot_direction: selectedSettings.shot_direction,
                    temperature: selectedSettings.temperature,
                    location: selectedSettings.location,
                    lighting: selectedSettings.lighting,
                    weather: selectedSettings.weather
                  }
                : shot
            )
          }
        }
        return scene
      })
    )
    setDrawerVisible(false)
  }

  const updateShotData = (shotId, key, value) => {
    console.log('updateShotData', shotId, key, value)
    setScenes(prevScenes =>
      prevScenes.map(scene => ({
        ...scene,
        shots: scene.shots.map(shot =>
          shot.id === shotId
            ? {
                ...shot,
                [key]: value
              }
            : shot
        )
      }))
    )
  }

  const updateSceneData = (sceneId, key, value) => {
    setScenes(prevScenes =>
      prevScenes.map(scene =>
        scene.id === sceneId ? { ...scene, [key]: value } : scene
      )
    )
  }

  const { isAnyImageGenerating, isAllImagesGenerated } = useMemo(() => {
    if (!scenes || !Array.isArray(scenes)) return false

    const isAnyImageGenerating = scenes.some(scene => {
      if (!scene.shots || !Array.isArray(scene.shots)) return false
      return scene.shots.some(shot => shot && shot.isGenerating === true)
    })

    const isAllImagesGenerated = scenes.every(scene => {
      if (!scene.shots || !Array.isArray(scene.shots)) return false
      return scene.shots.every(shot => shot && shot.imageSrc)
    })

    return {
      isAnyImageGenerating,
      isAllImagesGenerated
    }
  }, [scenes])

  const onClickGenerateAllImages = () => {
    Modal.confirm({
      title: 'Do you want to generate all images?',
      content: 'This will overwrite the current images.',
      centered: true,
      onOk() {
        generateAllImages(scenes)
      },
      onCancel() {
        console.log('Cancel')
      }
    })
  }

  const { totalImagesGenerated, totalImagesToGenerate } = useMemo(() => {
    let totalImagesGenerated = 0
    let totalImagesToGenerate = 0
    scenes.forEach(scene => {
      scene.shots.forEach(shot => {
        if (!shot.isGenerating && shot.imageSrc) {
          totalImagesGenerated++
        }
        totalImagesToGenerate++
      })
    })
    return {
      totalImagesGenerated,
      totalImagesToGenerate
    }
  }, [scenes])

  return (
    <div className="image-generation-container">
      <div
        className="header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <StepTitle
          isLoading={isLoading || isAnyImageGenerating}
          iconType="picture"
          title="Image Generation"
          description="Create images for your video"
          progress={{
            current: totalImagesGenerated,
            total: totalImagesToGenerate
          }}
        />

        {isAllImagesGenerated && (
          <Button
            type="primary"
            shape="round"
            size="large"
            className="shl-primary-btn"
            icon="play-circle"
            loading={isAnyImageGenerating}
            style={{ margin: '0 8px' }}
            onClick={onClickGenerateAllImages}
            disabled={isAnyImageGenerating}
          >
            Re-Generate All Images
          </Button>
        )}
      </div>
      {scenes.map(scene => (
        <div key={scene.id} className="scene-container">
          {/* Left Panel */}
          <div className="left-panel">
            <h2>{scene.title}</h2>
            <TextArea
              value={scene.description}
              rows={2}
              readOnly={isLoading}
              onChange={e => {
                const newDescription = e.target.value
                updateSceneData(scene.id, 'description', newDescription)
              }}
            />
          </div>
          <div className="right-panel">
            <div className="shots-grid">
              {scene.shots.map((shot, index) => (
                <div key={`${scene.id}-${shot.id}`} className="shot-card">
                  <h3 className="shot-title">Shot {index + 1}</h3>
                  <div
                    className="image-wrapper"
                    style={shot.error ? { border: '1px solid #ff4d4f', position: 'relative' } : { position: 'relative' }}
                  >
                    {shot.isGenerating && shot.progress !== 100 && (
                      <div className="progress-overlay">
                        <Spin style={{ color: '#fff' }} size="large" />
                        <span className="progress-text">
                          {shot.progress
                            ? 'Generated Successfully'
                            : 'Generating...'}
                        </span>
                      </div>
                    )}
                    {!shot.isGenerating && shot.error && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: '#fff',
                          border: '1px solid #ff4d4f',
                          color: '#ff4d4f',
                          borderRadius: 4,
                          padding: '2px 6px',
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        Task failed. Please regenerate.
                      </div>
                    )}
                    {shot.imageSrc && (
                      <ImagePreview
                        src={shot.imageSrc || '/api/placeholder/400/300'}
                        alt={`Shot ${shot.id}`}
                      />
                    )}
                  </div>
                  {/* Remove duplicated error text below to keep UI clean */}
                  <CommentInput
                    disabled={shot.isGenerating}
                    text={shot.shot_direction}
                    placeholder="Write your image prompt..."
                    characters={formData.characterList}
                    onChange={value =>
                      updateShotData(shot.id, 'shot_direction', value)
                    }
                  />
                  <div className="card-actions">
                    <Button
                      size="small"
                      icon="reload"
                      onClick={() => handleRegenerateImage(scene.id, shot.id)}
                      disabled={shot.isGenerating}
                    >
                      Regenerate Image
                    </Button>
                    <Button
                      size="small"
                      icon="setting"
                      onClick={() => handleEditSettings(scene.id, shot.id)}
                      disabled={shot.isGenerating}
                    >
                      Shot Editor
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      {/* Settings Drawer (unchanged from previous version) */}
      <Drawer
        title="Edit Shot Settings"
        placement="right"
        closable={true}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        width={400}
      >
        {selectedSettings && (
          <div className="settings-drawer-content">
            <CommentInput
              text={selectedSettings.shot_direction}
              placeholder="Write your image prompt..."
              characters={formData.characterList}
              onChange={value => updateSettings('shot_direction', value)}
            />
            <div className="setting-group">
              <label>Location</label>
              <Input
                value={selectedSettings.location}
                onChange={e => updateSettings('location', e.target.value)}
              />
            </div>

            <div className="setting-group">
              <label>Lighting</label>
              <Input
                value={selectedSettings.lighting}
                onChange={e => updateSettings('lighting', e.target.value)}
              />
            </div>

            <div className="setting-group">
              <label>Weather</label>
              <Input
                value={selectedSettings.weather}
                onChange={e => updateSettings('weather', e.target.value)}
              />
            </div>

            <div className="setting-group">
              <label>
                Temperature (Creativity)
                <Tooltip title="Low values (0.1) create more predictable images. High values (1.0) create more creative and varied results.">
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </label>
              <Slider
                min={0.1}
                max={1}
                step={0.1}
                value={
                  (selectedSettings && selectedSettings.temperature) || 0.7
                }
                onChange={value => updateSettings('temperature', value)}
                marks={{
                  0.1: '0.1',
                  0.5: '0.5',
                  1: '1'
                }}
              />
            </div>

            <Button
              type="primary"
              onClick={saveSettings}
              style={{ marginTop: 16 }}
            >
              Save Settings
            </Button>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default ImageGeneration
